import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { dailyActivitiesAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Check, Lock, ChevronRight, Sparkles, Brain, Heart, Shield, PenLine,
  Flame, Award, Loader2, ArrowLeft, Crown,
} from 'lucide-react';

const CATEGORY_META = {
  consciencia: { label: 'Consciência', icon: Brain, color: '#818cf8' },
  comportamento: { label: 'Comportamento', icon: Shield, color: '#34d399' },
  emocional: { label: 'Emocional', icon: Heart, color: '#f472b6' },
  decisao: { label: 'Decisão', icon: Sparkles, color: '#fbbf24' },
  escrita: { label: 'Escrita Terapêutica', icon: PenLine, color: '#a78bfa' },
};

function ProgressRing({ completed, total, size = 80 }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  const r = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="progress-ring">
      <svg className="w-full h-full -rotate-90" viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="5" />
        <motion.circle
          cx={size/2} cy={size/2} r={r} fill="none"
          stroke={pct >= 100 ? '#22c55e' : '#8b5cf6'}
          strokeWidth="5" strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold text-white">{completed}/{total}</span>
      </div>
    </div>
  );
}

function ActivityCard({ activity, index, onAnswer, savedAnswer }) {
  const [writing, setWriting] = useState(savedAnswer || '');
  const [selected, setSelected] = useState(savedAnswer || null);
  const meta = CATEGORY_META[activity.category] || CATEGORY_META.consciencia;
  const Icon = meta.icon;
  const isCompleted = !!savedAnswer;
  const isWriting = activity.type === 'writing';

  const handleSelect = (letter) => {
    setSelected(letter);
    onAnswer(activity.id, activity.block, letter);
  };

  const handleWritingSubmit = () => {
    if (writing.trim().length < 5) {
      toast.error('Escreva pelo menos algumas palavras');
      return;
    }
    onAnswer(activity.id, activity.block, writing.trim());
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      className="rounded-2xl p-[1px]"
      style={{ background: isCompleted ? 'rgba(34,197,94,0.3)' : `linear-gradient(135deg, ${meta.color}33, transparent)` }}
      data-testid={`activity-card-${activity.id}`}
    >
      <div className="rounded-2xl p-5" style={{ background: 'rgba(15,15,35,0.95)' }}>
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${meta.color}22` }}>
            {isCompleted ? <Check className="h-4 w-4 text-green-400" /> : <Icon className="h-4 w-4" style={{ color: meta.color }} />}
          </div>
          <div className="flex-1">
            <span className="text-xs font-medium uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
          </div>
          {isCompleted && <span className="text-xs text-green-400 font-medium">Concluído</span>}
        </div>

        {/* Question */}
        <p className="text-sm sm:text-base text-gray-200 mb-4 leading-relaxed">{activity.text}</p>

        {/* Options or Writing */}
        {isWriting ? (
          <div>
            <textarea
              value={writing}
              onChange={(e) => setWriting(e.target.value)}
              placeholder={activity.placeholder || 'Escreva aqui...'}
              disabled={isCompleted}
              className="w-full h-28 p-3 rounded-xl text-sm bg-white/5 border border-white/10 text-white placeholder:text-gray-600 resize-none focus:outline-none focus:border-purple-500/50 transition-colors disabled:opacity-60"
              data-testid={`writing-input-${activity.id}`}
            />
            {!isCompleted && (
              <button
                onClick={handleWritingSubmit}
                disabled={writing.trim().length < 5}
                className="mt-2 px-5 py-2 rounded-xl text-sm font-medium text-white disabled:opacity-30 transition-all"
                style={{ background: writing.trim().length >= 5 ? `linear-gradient(135deg, ${meta.color}, ${meta.color}cc)` : 'rgba(255,255,255,0.05)' }}
                data-testid={`writing-submit-${activity.id}`}
              >
                Salvar Reflexão
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {activity.options?.map((opt) => {
              const isActive = selected === opt.letter;
              return (
                <button
                  key={opt.letter}
                  onClick={() => !isCompleted && handleSelect(opt.letter)}
                  disabled={isCompleted}
                  className={`w-full text-left p-3 rounded-xl border text-sm transition-all ${
                    isActive ? 'border-transparent text-white font-medium' : 'border-white/8 text-gray-400 hover:border-white/15 hover:bg-white/3'
                  } ${isCompleted ? 'opacity-70' : ''}`}
                  style={isActive ? { background: `${meta.color}15`, borderColor: `${meta.color}66` } : {}}
                  data-testid={`option-${activity.id}-${opt.letter}`}
                >
                  <span className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                      isActive ? '' : 'border-white/15'
                    }`} style={isActive ? { borderColor: meta.color, background: meta.color, color: '#000' } : {}}>
                      {isActive ? <Check className="h-3 w-3" /> : opt.letter}
                    </span>
                    {opt.text}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function BlockSection({ title, activities, blockNum, locked, onAnswer, onUnlock }) {
  if (locked) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl p-6 text-center" style={{ background: 'rgba(15,15,35,0.8)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid="block2-locked">
        <Lock className="h-10 w-10 text-gray-600 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-gray-400 mb-2">Bloco 2 — Exclusivo Premium</h3>
        <p className="text-sm text-gray-600 mb-4">Desbloqueie mais 5 atividades avançadas e ganhe tokens bônus.</p>
        <button onClick={onUnlock} className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all" style={{ background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)' }} data-testid="upgrade-premium-btn">
          <Crown className="h-4 w-4 inline mr-2" />Desbloquear Premium
        </button>
      </motion.div>
    );
  }

  const completed = activities.filter(a => a.saved_answer || a.savedAnswer).length;
  const total = activities.length;

  return (
    <div data-testid={`block-${blockNum}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: blockNum === 1 ? 'rgba(99,102,241,0.15)' : 'rgba(245,158,11,0.15)' }}>
          <span className="text-sm font-bold" style={{ color: blockNum === 1 ? '#818cf8' : '#fbbf24' }}>{blockNum}</span>
        </div>
        <div className="flex-1">
          <h3 className="text-base font-bold text-white">{title}</h3>
          <p className="text-xs text-gray-500">{completed}/{total} concluídas</p>
        </div>
        {completed >= total && <span className="text-xs text-green-400 font-semibold flex items-center gap-1"><Check className="h-3 w-3" /> Completo</span>}
      </div>
      <div className="space-y-3">
        {activities.map((act, i) => (
          <ActivityCard
            key={act.id}
            activity={act}
            index={i}
            onAnswer={onAnswer}
            savedAnswer={act.saved_answer || act.savedAnswer}
          />
        ))}
      </div>
    </div>
  );
}

export default function DailyActivities() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pendingAnswers, setPendingAnswers] = useState([]);

  const loadActivities = useCallback(async () => {
    try {
      const res = await dailyActivitiesAPI.getToday();
      setData(res.data);
    } catch (err) {
      toast.error('Erro ao carregar atividades');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) loadActivities();
  }, [authLoading, user, loadActivities]);

  const handleAnswer = async (activityId, block, answer) => {
    setSubmitting(true);
    try {
      const res = await dailyActivitiesAPI.submit([{ activity_id: activityId, block, answer }]);
      const result = res.data;
      if (result.tokens_awarded_now > 0) {
        toast.success(`+${result.tokens_awarded_now} AWA!`, { description: result.block1_completed && result.block2_completed ? 'Parabéns! Todas as atividades do dia!' : 'Bloco completado!' });
      }
      // Reload to update state
      await loadActivities();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar resposta');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #06061a 0%, #0c0c2d 50%, #0a0a1a 100%)' }}>
        <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
      </div>
    );
  }

  if (!data) return null;

  const totalAnswered = data.total_answered;
  const totalActivities = data.total_activities;
  const allDone = data.block1_completed && (data.block2_completed || !data.can_access_block2);

  return (
    <div className="min-h-screen pb-12" style={{ background: 'linear-gradient(180deg, #06061a 0%, #0c0c2d 50%, #0a0a1a 100%)' }} data-testid="daily-activities-page">
      <div className="max-w-2xl mx-auto px-4 pt-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate('/feed')} className="text-gray-500 hover:text-gray-300 transition-colors" data-testid="back-to-feed">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-white">Atividades do Dia</h1>
            <p className="text-xs text-gray-500">{new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          </div>
          <ProgressRing completed={totalAnswered} total={totalActivities} />
        </div>

        {/* Streak / Status bar */}
        <div className="flex items-center gap-3 mb-6 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <Flame className="h-5 w-5 text-orange-400" />
          <div className="flex-1">
            <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <motion.div
                className="h-full rounded-full"
                style={{ background: allDone ? 'linear-gradient(90deg, #22c55e, #16a34a)' : 'linear-gradient(90deg, #8b5cf6, #a78bfa)' }}
                initial={{ width: 0 }}
                animate={{ width: `${totalActivities > 0 ? (totalAnswered / totalActivities) * 100 : 0}%` }}
                transition={{ duration: 0.6 }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">{totalAnswered}/{totalActivities} atividades · {data.tokens_earned_today} AWA ganhos hoje</p>
          </div>
          {allDone && <Award className="h-5 w-5 text-green-400" />}
        </div>

        {/* All done banner */}
        {allDone && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mb-6 p-4 rounded-xl text-center" style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)' }} data-testid="all-done-banner">
            <Award className="h-8 w-8 text-green-400 mx-auto mb-2" />
            <p className="text-green-300 font-bold">Parabéns! Todas as atividades de hoje foram concluídas!</p>
            <p className="text-green-400/60 text-xs mt-1">Volte amanhã para novas atividades e mais AWA.</p>
          </motion.div>
        )}

        {/* Block 1 */}
        <div className="mb-8">
          <BlockSection
            title="Bloco 1 — Atividades Essenciais"
            activities={data.block1}
            blockNum={1}
            locked={false}
            onAnswer={handleAnswer}
          />
        </div>

        {/* Block 2 */}
        <div className="mb-8">
          <BlockSection
            title="Bloco 2 — Transformação Profunda"
            activities={data.block2}
            blockNum={2}
            locked={!data.can_access_block2}
            onAnswer={handleAnswer}
            onUnlock={() => navigate('/checkout')}
          />
        </div>
      </div>

      {/* Submitting overlay */}
      {submitting && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="p-4 rounded-xl" style={{ background: 'rgba(15,15,35,0.95)' }}>
            <Loader2 className="h-6 w-6 animate-spin text-purple-400 mx-auto" />
            <p className="text-xs text-gray-400 mt-2">Salvando...</p>
          </div>
        </div>
      )}
    </div>
  );
}
